import React, { useContext, useEffect, useRef, useState } from 'react'
import { View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from '@ui-kitten/components'
import { Button } from '@components/base'
import Stripe, { StripeCardElement, Stripe as StripeClient } from '@config/stripe.web'
import { Apollo } from '@config/apollo'
import { NavigationContainer } from '@src/screens/Common/containers'
import { StyleService, useStyleSheet } from '@src/style/service'
import { userSelector } from '@src/selectors/app'
import { createPaymentMethod } from '@utils/payment.web'
import { useGoBack } from '@src/utils/navigation'
import { NavigatorContext, useSnack } from '@src/utils/navigatorContext'

export const CreatePaymentMethod = () => {
  const dispatch = useDispatch()
  const showSnack = useSnack()
  const goBack = useGoBack()
  const styles = useStyleSheet(themedStyle)
  const theme = useTheme()
  const primaryTextColor = theme['theme.text.primary']
  const tertiaryTextColor = theme['theme.text.tertiary']
  const errorColor = theme['theme.error.base']

  const user = useSelector(userSelector)
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const isSubmittingRef = useRef(false)
  const cardContainerRef = useRef<any>(null)
  const stripeRef = useRef<StripeClient | null>(null)
  const cardElementRef = useRef<StripeCardElement | null>(null)
  const [cardComplete, setCardComplete] = useState(false)
  const [cardReady, setCardReady] = useState(false)

  const onSubmit = async () => {
    if (!user || !stripeRef.current || !cardElementRef.current) {
      return
    }

    if (!cardComplete) {
      cardElementRef.current.focus()
      return
    }

    if (isSubmittingRef.current) {
      return
    }

    isSubmittingRef.current = true
    enableLoader()

    try {
      await createPaymentMethod({
        user,
        dispatch,
        card: {
          element: cardElementRef.current,
          stripe: stripeRef.current,
        },
      })
      goBack()
    } catch (error: any) {
      showSnack(error?.message || "Couldn't create payment method!", null, 'error')
    } finally {
      isSubmittingRef.current = false
      disableLoader()
    }
  }

  useEffect(() => {
    let isMounted = true

    const mountCardElement = async () => {
      if (!cardContainerRef.current) {
        return
      }

      const stripe = await Stripe.configure(Apollo.endpointEnv)

      if (!isMounted || !cardContainerRef.current) {
        return
      }

      const elements = stripe.elements()
      const cardElement = elements.create('card', {
        hidePostalCode: true,
        style: {
          base: {
            color: primaryTextColor,
            fontFamily: 'NBInternationalProReg, sans-serif',
            fontSize: '17px',
            '::placeholder': {
              color: tertiaryTextColor,
            },
          },
          invalid: {
            color: errorColor,
          },
        },
      })

      cardElement.on('change', (event) => {
        setCardComplete(!!event?.complete)
      })
      cardElement.on('ready', () => {
        setCardReady(true)
        cardElement.focus()
      })
      cardElement.mount(cardContainerRef.current)
      stripeRef.current = stripe
      cardElementRef.current = cardElement
    }

    mountCardElement().catch((error) => {
      showSnack(error?.message || 'Unable to load Stripe', null, 'error')
    })

    return () => {
      isMounted = false
      cardElementRef.current?.destroy()
      cardElementRef.current = null
      stripeRef.current = null
    }
  }, [errorColor, primaryTextColor, showSnack, tertiaryTextColor])

  return (
    <NavigationContainer title="Payment method" goBack={goBack}>
      <SafeAreaView edges={['bottom']} style={styles.content}>
        <View style={styles.cardContainer}>
          <div ref={cardContainerRef} style={{ width: '100%' }} />
        </View>
        <Button
          allowPressWhenDisabled
          accessibilityLabel="Add Card"
          disabled={!cardComplete || !cardReady}
          size="block"
          type="primary"
          onPress={onSubmit}
        >
          Add Card
        </Button>
      </SafeAreaView>
    </NavigationContainer>
  )
}

export default CreatePaymentMethod

const themedStyle = StyleService.create({
  content: {
    flex: 1,
    justifyContent: 'space-between',
    paddingTop: 24,
    paddingHorizontal: 16,
    paddingBottom: 16,
    backgroundColor: 'theme.background',
  },
  cardContainer: {
    height: 64,
    justifyContent: 'center',
    borderRadius: 16,
    backgroundColor: 'theme.surface.base2',
    paddingHorizontal: 16,
  },
})
